/**
 * Base application error with structured metadata
 */
export class AppError extends Error {
  public readonly statusCode: number
  public readonly errorCode: string
  public readonly isOperational: boolean
  public readonly context?: Record<string, unknown>

  constructor(
    message: string,
    statusCode: number = 500,
    errorCode: string = 'INTERNAL_ERROR',
    context?: Record<string, unknown>,
    isOperational: boolean = true
  ) {
    super(message)
    this.name = this.constructor.name
    this.statusCode = statusCode
    this.errorCode = errorCode
    this.context = context
    this.isOperational = isOperational

    // Capture stack trace (exclude constructor call from stack)
    Error.captureStackTrace(this, this.constructor)
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      errorCode: this.errorCode,
      statusCode: this.statusCode,
      context: this.context,
      stack: process.env['NODE_ENV'] === 'development' ? this.stack : undefined
    }
  }
}

/**
 * 400 Bad Request - Client sent invalid data
 * 
 * @example
 * throw new ValidationError('Invalid email format', { email: 'not-an-email' })
 */
export class ValidationError extends AppError {
  constructor(message: string, context?: Record<string, unknown>) {
    super(message, 400, 'VALIDATION_ERROR', context)
  }
}

/**
 * 404 Not Found - Resource does not exist
 * 
 * @example
 * throw new NotFoundError('User not found', { userId: '123' })
 */
export class NotFoundError extends AppError {
  constructor(message: string, context?: Record<string, unknown>) {
    super(message, 404, 'NOT_FOUND', context)
  }
}

/**
 * 409 Conflict - Resource already exists or state conflict
 * 
 * @example
 * throw new ConflictError('User already has this badge', { userId: '123', badgeId: 'abc' })
 */
export class ConflictError extends AppError {
  constructor(message: string, context?: Record<string, unknown>) {
    super(message, 409, 'CONFLICT', context)
  }
}

/**
 * 401 Unauthorized - Authentication required or failed
 * 
 * @example
 * throw new UnauthorizedError('Invalid token', { token: 'expired' })
 */
export class UnauthorizedError extends AppError {
  constructor(message: string, context?: Record<string, unknown>) {
    super(message, 401, 'UNAUTHORIZED', context)
  }
}

/**
 * 403 Forbidden - User lacks permission
 * 
 * @example
 * throw new ForbiddenError('Admin access required', { userId: '123', requiredRole: 'admin' })
 */
export class ForbiddenError extends AppError {
  constructor(message: string, context?: Record<string, unknown>) {
    super(message, 403, 'FORBIDDEN', context)
  }
}

/**
 * 429 Too Many Requests - Rate limit exceeded
 * 
 * @example
 * throw new RateLimitError('Too many requests', { userId: '123', limit: 100, retryAfter: 60 })
 */
export class RateLimitError extends AppError {
  constructor(message: string, context?: Record<string, unknown>) {
    super(message, 429, 'RATE_LIMIT_EXCEEDED', context)
  }
}

/**
 * 503 Service Unavailable - External service failure
 * 
 * @example
 * throw new ServiceUnavailableError('OpenAI API timeout', { service: 'openai', timeout: 30000 })
 */
export class ServiceUnavailableError extends AppError {
  constructor(message: string, context?: Record<string, unknown>) {
    super(message, 503, 'SERVICE_UNAVAILABLE', context)
  }
}

/**
 * 500 Internal Server Error - Unexpected server error
 * 
 * @example
 * throw new InternalError('Unexpected database error', { operation: 'updateUser', error })
 */
export class InternalError extends AppError {
  constructor(message: string, context?: Record<string, unknown>) {
    super(message, 500, 'INTERNAL_ERROR', context, false) // Not operational
  }
}
